import {
  AlertTriangleIcon,
  CalendarIcon,
  CheckIcon,
  XIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Navigate, NavLink, Route, Routes } from "react-router-dom";
import {
  useDomainRouterMutation,
  useDomainRouterQuery,
} from "../../../state/domainRouterHooks";
import {
  ModuleSectionNav,
  type ModuleSectionNavItem,
} from "../../../components/navigation/ModuleSectionNav";
import { SupportErrorMessage } from "../../../components/overlays/EngineeringSupportBanner";

type AbsenceRecord = {
  absenceId: string;
  teacherId: string;
  teacherName: string;
  division: string;
  gradeLevel: string;
  date: string;
  coverageLabel: string;
  reason: string;
  lessonPlanStatus: "attached" | "not-provided";
  assignmentStatus: "unassigned" | "assigned" | "partial";
  assignedSubstituteId?: string | null;
  assignedSubstituteName?: string | null;
  partialDetails?: string | null;
  hours: number;
};

type SubstitutePoolRecord = {
  substituteId: string;
  name: string;
  divisions: string[];
  specialties: string[];
  hourlyRateCents: number;
  status: "active" | "inactive";
  availability: string;
};

type ScheduleItem = {
  absenceId: string;
  substituteName: string;
  coverageLabel: string;
  date: string;
  status: string;
};

type PayrollItem = {
  substituteId: string;
  substituteName: string;
  hours: number;
  amountCents: number;
};

const SUBSTITUTE_SECTION_ITEMS: ModuleSectionNavItem[] = [
  {
    id: "assignments",
    label: "Assignments",
    to: "/school/substitutes",
    end: true,
  },
  { id: "absences", label: "Absences", to: "/school/substitutes/absences" },
  { id: "pool", label: "Pool", to: "/school/substitutes/pool" },
  { id: "schedule", label: "Schedule", to: "/school/substitutes/schedule" },
  { id: "payroll", label: "Payroll", to: "/school/substitutes/payroll" },
];

const todayLabel = () =>
  new Date().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

const formatCurrency = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

const Shell = ({
  title,
  children,
  testId,
}: {
  title: string;
  children: React.ReactNode;
  testId: string;
}) => (
  <main
    data-testid={testId}
    data-extracted-from="source-faithful:school-substitute-management-v1.0"
    data-route-family="source-faithful"
    data-route-ready="true"
    className="source-faithful-shell"
    style={
      { "--ystnj-source-faithful-max-width": "1024px" } as React.CSSProperties
    }
  >
    <div className="source-faithful-container">
      <div className="source-faithful-header">
        <div>
          <h2 className="text-[24px] font-semibold text-[var(--iron-grey)]">
            {title}
          </h2>
          <p className="mt-1 text-[13px] font-medium uppercase tracking-[0.05em] text-[var(--text-secondary)]">
            Persisted substitute operations · {todayLabel()}
          </p>
        </div>
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-md border border-[var(--border)] bg-white px-4 py-2.5 text-sm font-medium text-[var(--iron-grey)]"
        >
          <CalendarIcon size={16} />
          {todayLabel()}
        </button>
      </div>
      <ModuleSectionNav
        items={SUBSTITUTE_SECTION_ITEMS}
        ariaLabel="Substitute sections"
        desktopClassName="source-faithful-tabs"
        testIds={{
          root: "substitutes-section-nav",
          itemPrefix: "substitutes-section-nav-item",
          mobileTrigger: "substitutes-section-nav-mobile-trigger",
          mobileDialog: "substitutes-section-nav-mobile-dialog",
          mobileItemPrefix: "substitutes-section-nav-mobile-item",
        }}
      />
      {children}
    </div>
  </main>
);

const EmptyState = ({ message }: { message: string }) => (
  <div className="rounded-[12px] border border-dashed border-[var(--border)] bg-[var(--surface-light)] px-6 py-10 text-center text-sm text-[var(--text-secondary)]">
    {message}
  </div>
);

const AbsenceCard = ({
  absence,
  onAssign,
  assigning,
}: {
  absence: AbsenceRecord;
  onAssign: (absence: AbsenceRecord) => void;
  assigning: boolean;
}) => {
  const isUrgent =
    absence.assignmentStatus === "unassigned" &&
    absence.date <= new Date().toISOString().slice(0, 10);
  const lessonAttached = absence.lessonPlanStatus === "attached";
  const statusLabel =
    absence.assignmentStatus === "assigned"
      ? `Assigned: ${absence.assignedSubstituteName ?? "Substitute"}`
      : absence.assignmentStatus === "partial"
        ? `Partial: ${absence.partialDetails ?? "Coverage started"}`
        : "Unassigned";

  return (
    <article
      className="rounded-[12px] border border-[var(--border)] bg-[var(--surface-light)] p-5 shadow-sm"
      style={{
        borderLeft: isUrgent
          ? "4px solid var(--strawberry)"
          : absence.assignmentStatus === "partial"
            ? "4px solid var(--coral)"
            : undefined,
      }}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[16px] font-semibold text-[var(--iron-grey)]">
            {absence.teacherName}
          </p>
          <p className="text-sm text-[var(--iron-grey)]">
            {absence.date} · {absence.coverageLabel}
          </p>
          <p className="text-[13px] text-[var(--text-secondary)]">
            {absence.division} · {absence.gradeLevel}
          </p>
        </div>
        <span className="rounded-md bg-[var(--muted)] px-3 py-1 text-sm font-medium text-[var(--iron-grey)]">
          {absence.reason}
        </span>
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-4 border-t border-[var(--border)] pt-4">
        <div
          className="flex items-center gap-2 text-sm font-medium"
          style={{
            color: lessonAttached ? "var(--jungle)" : "var(--strawberry)",
          }}
        >
          {lessonAttached ? <CheckIcon size={16} /> : <XIcon size={16} />}
          {lessonAttached ? "Lesson plan attached" : "Lesson plan missing"}
        </div>
        <div className="text-xs font-semibold uppercase tracking-[0.05em] text-[var(--text-secondary)]">
          {statusLabel}
        </div>
        <button
          type="button"
          onClick={() => onAssign(absence)}
          disabled={assigning}
          className={`rounded-md px-3 py-2 text-sm font-medium ${
            absence.assignmentStatus === "assigned"
              ? "border border-[var(--border)] text-[var(--iron-grey)]"
              : "bg-[var(--primary)] text-white"
          } disabled:cursor-not-allowed disabled:opacity-70`}
        >
          {assigning
            ? "Saving…"
            : absence.assignmentStatus === "assigned"
              ? "Reassign"
              : "Assign substitute"}
        </button>
      </div>
    </article>
  );
};

const useSubstituteData = () => {
  const dashboardQuery = useDomainRouterQuery("getSubstituteDashboard", {});
  const absencesQuery = useDomainRouterQuery("listOpenAbsences", {});
  const poolQuery = useDomainRouterQuery("listSubstitutePool", {});
  const scheduleQuery = useDomainRouterQuery("listSubstituteSchedule", {});
  const payrollQuery = useDomainRouterQuery("getSubstitutePayroll", {});
  const dashboard =
    (dashboardQuery.data as
      | {
          urgentCount?: number;
          assignmentsToday?: number;
          openAbsences?: number;
          substitutePoolSize?: number;
          absences?: AbsenceRecord[];
        }
      | undefined) ?? {};
  const absences =
    (absencesQuery.data as { items?: AbsenceRecord[] } | undefined)?.items ??
    dashboard.absences ??
    [];
  const pool =
    (poolQuery.data as { items?: SubstitutePoolRecord[] } | undefined)?.items ??
    [];
  const schedule =
    (scheduleQuery.data as { items?: ScheduleItem[] } | undefined)?.items ?? [];
  const payroll =
    (payrollQuery.data as { items?: PayrollItem[] } | undefined)?.items ?? [];

  return {
    dashboardQuery,
    dashboard,
    absences,
    pool,
    schedule,
    payroll,
  };
};

const AssignmentsPage = () => {
  const { dashboardQuery, dashboard, absences } = useSubstituteData();
  const [assignSubstitute, assignState] =
    useDomainRouterMutation("assignSubstitute");
  const [activeAbsenceId, setActiveAbsenceId] = useState("");

  const handleAssign = async (absence: AbsenceRecord) => {
    setActiveAbsenceId(absence.absenceId);
    try {
      await assignSubstitute({
        input: {
          absenceId: absence.absenceId,
          date: absence.date,
        },
      });
    } finally {
      setActiveAbsenceId("");
    }
  };

  return (
    <Shell
      title="Substitute assignments"
      testId="substitutes-source-faithful-root"
    >
      <div className="source-faithful-kpiGrid">
        {[
          ["Urgent today", String(dashboard.urgentCount ?? 0)],
          ["Assignments today", String(dashboard.assignmentsToday ?? 0)],
          ["Open absences", String(dashboard.openAbsences ?? 0)],
          ["Active substitutes", String(dashboard.substitutePoolSize ?? 0)],
        ].map(([label, value]) => (
          <article
            key={label}
            className="rounded-[12px] border border-[var(--border)] bg-[var(--surface-light)] p-5 shadow-sm"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.05em] text-[var(--text-secondary)]">
              {label}
            </p>
            <p className="mt-3 text-2xl font-semibold text-[var(--iron-grey)]">
              {value}
            </p>
          </article>
        ))}
      </div>
      {(dashboard.urgentCount ?? 0) > 0 ? (
        <div
          className="mt-4 rounded-md bg-[var(--warning-background)] px-4 py-3"
          style={{ borderLeft: "4px solid var(--coral)" }}
        >
          <div className="flex items-center gap-3 text-[12px] font-semibold uppercase tracking-[0.05em] text-[var(--iron-grey)]">
            <AlertTriangleIcon size={18} color="var(--coral)" />
            {dashboard.urgentCount ?? 0} same-day absence
            {(dashboard.urgentCount ?? 0) === 1 ? "" : "s"} need coverage
          </div>
        </div>
      ) : null}
      {dashboardQuery.isLoading ? (
        <div className="mt-4">
          <EmptyState message="Loading persisted substitute coverage data…" />
        </div>
      ) : !absences.length ? (
        <div className="mt-4">
          <EmptyState message="No open substitute assignments are waiting right now." />
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          {absences.map((absence) => (
            <AbsenceCard
              key={absence.absenceId}
              absence={absence}
              onAssign={(record) => void handleAssign(record)}
              assigning={
                assignState.isLoading && activeAbsenceId === absence.absenceId
              }
            />
          ))}
          {assignState.error ? (
            <SupportErrorMessage className="text-sm">
              The substitute assignment could not be saved.
            </SupportErrorMessage>
          ) : null}
        </div>
      )}
    </Shell>
  );
};

const AbsencesPage = () => {
  const { absences } = useSubstituteData();
  const [assignSubstitute, assignState] =
    useDomainRouterMutation("assignSubstitute");
  const [activeAbsenceId, setActiveAbsenceId] = useState("");

  const handleAssign = async (absence: AbsenceRecord) => {
    setActiveAbsenceId(absence.absenceId);
    try {
      await assignSubstitute({
        input: {
          absenceId: absence.absenceId,
          date: absence.date,
        },
      });
    } finally {
      setActiveAbsenceId("");
    }
  };

  return (
    <Shell title="Open absences" testId="substitutes-source-faithful-absences">
      {!absences.length ? (
        <EmptyState message="No open absences are waiting for coverage." />
      ) : (
        <div className="space-y-4">
          {absences.map((absence) => (
            <AbsenceCard
              key={absence.absenceId}
              absence={absence}
              onAssign={(record) => void handleAssign(record)}
              assigning={
                assignState.isLoading && activeAbsenceId === absence.absenceId
              }
            />
          ))}
        </div>
      )}
    </Shell>
  );
};

const PoolPage = () => {
  const { pool } = useSubstituteData();
  return (
    <Shell
      title="Qualified substitute pool"
      testId="substitutes-source-faithful-pool"
    >
      {!pool.length ? (
        <EmptyState message="No persisted substitute profiles are available." />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {pool.map((profile) => (
            <article
              key={profile.substituteId}
              className="rounded-[12px] border border-[var(--border)] bg-[var(--surface-light)] p-5 shadow-sm"
            >
              <div className="flex items-center justify-between gap-4">
                <p className="text-[16px] font-semibold text-[var(--iron-grey)]">
                  {profile.name}
                </p>
                <span
                  className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.05em] ${
                    profile.status === "active"
                      ? "bg-[rgba(40,226,167,0.15)] text-[var(--jungle)]"
                      : "bg-[rgba(255, 95, 95,0.18)] text-[var(--coral)]"
                  }`}
                >
                  {profile.status}
                </span>
              </div>
              <p className="mt-2 text-sm text-[var(--text-secondary)]">
                {profile.divisions.join(", ")} ·{" "}
                {profile.specialties.join(", ")}
              </p>
              <p className="mt-4 text-xs font-semibold uppercase tracking-[0.05em] text-[var(--primary)]">
                {profile.availability}
              </p>
              <p className="mt-2 text-sm text-[var(--text-secondary)]">
                {formatCurrency(profile.hourlyRateCents)}/hr
              </p>
            </article>
          ))}
        </div>
      )}
    </Shell>
  );
};

const SchedulePage = () => {
  const { schedule } = useSubstituteData();
  return (
    <Shell
      title="Substitute schedule"
      testId="substitutes-source-faithful-schedule"
    >
      {!schedule.length ? (
        <EmptyState message="No substitute schedule rows are available yet." />
      ) : (
        <div className="overflow-hidden rounded-[12px] border border-[var(--border)] bg-[var(--surface-light)] shadow-sm">
          <div className="source-faithful-tableScroll">
            <table className="min-w-[620px] divide-y divide-[var(--border)] text-sm">
              <thead className="bg-[var(--muted)] text-[var(--text-secondary)]">
                <tr>
                  {["Date", "Coverage", "Substitute", "Status"].map((label) => (
                    <th
                      key={label}
                      className="px-4 py-3 text-left font-semibold uppercase tracking-[0.05em]"
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {schedule.map((row) => (
                  <tr key={row.absenceId}>
                    <td className="px-4 py-4 text-[var(--iron-grey)]">
                      {row.date}
                    </td>
                    <td className="px-4 py-4 text-[var(--iron-grey)]">
                      {row.coverageLabel}
                    </td>
                    <td className="px-4 py-4 text-[var(--iron-grey)]">
                      {row.substituteName}
                    </td>
                    <td className="px-4 py-4 text-[var(--iron-grey)]">
                      <span className="rounded-full bg-[rgba(40,226,167,0.15)] px-3 py-1 text-xs font-semibold text-[var(--jungle)]">
                        {row.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Shell>
  );
};

const PayrollPage = () => {
  const { payroll } = useSubstituteData();
  const totals = useMemo(
    () =>
      payroll.reduce(
        (summary, item) => ({
          hours: summary.hours + item.hours,
          amountCents: summary.amountCents + item.amountCents,
        }),
        { hours: 0, amountCents: 0 },
      ),
    [payroll],
  );

  return (
    <Shell
      title="Payroll export review"
      testId="substitutes-source-faithful-payroll"
    >
      <div className="source-faithful-kpiGrid">
        {[
          ["Hours pending", totals.hours.toFixed(1)],
          ["Approved substitutes", String(payroll.length)],
          ["Payout total", formatCurrency(totals.amountCents)],
        ].map(([label, value]) => (
          <article
            key={label}
            className="rounded-[12px] border border-[var(--border)] bg-[var(--surface-light)] p-5 shadow-sm"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.05em] text-[var(--text-secondary)]">
              {label}
            </p>
            <p className="mt-3 text-2xl font-semibold text-[var(--iron-grey)]">
              {value}
            </p>
          </article>
        ))}
      </div>
      {!payroll.length ? (
        <div className="mt-4">
          <EmptyState message="No substitute payroll rows are ready yet." />
        </div>
      ) : (
        <div className="mt-4 overflow-hidden rounded-[12px] border border-[var(--border)] bg-[var(--surface-light)] shadow-sm">
          {payroll.map((item, index) => (
            <div
              key={item.substituteId}
              className={`flex items-center justify-between gap-4 px-4 py-4 ${index < payroll.length - 1 ? "border-b border-[var(--border)]" : ""}`}
            >
              <div>
                <p className="font-semibold text-[var(--iron-grey)]">
                  {item.substituteName}
                </p>
                <p className="text-sm text-[var(--text-secondary)]">
                  {item.hours.toFixed(1)} hours
                </p>
              </div>
              <div className="text-right">
                <p className="font-semibold text-[var(--iron-grey)]">
                  {formatCurrency(item.amountCents)}
                </p>
                <p className="text-xs text-[var(--text-secondary)]">
                  Persisted payroll total
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Shell>
  );
};

export function SubstituteSourceFaithfulRoutes() {
  return (
    <Routes>
      <Route index element={<AssignmentsPage />} />
      <Route path="absences" element={<AbsencesPage />} />
      <Route path="pool" element={<PoolPage />} />
      <Route path="assignments" element={<AssignmentsPage />} />
      <Route path="schedule" element={<SchedulePage />} />
      <Route path="payroll" element={<PayrollPage />} />
      <Route path="*" element={<Navigate to="." replace />} />
    </Routes>
  );
}
